import { memo, useEffect, useState } from 'react';

import { Button, Flex, Form, Typography, Upload } from 'antd';

import { ExclamationOutlined, UploadOutlined } from '@ant-design/icons';
import classnames from 'classnames';

import messages from '../../../constants/messages';

import { useDataValidator } from '../../../hooks/useDataValidator';
import { useExcelParser } from '../../../hooks/useExcelParser';

import AppModal from '../../../components/AppModal';

import styles from './styles.module.scss';

import CsvDownload from './CsvDownload';

const { Text } = Typography;

function UploadModal({
  children,
  description,
  fileName,
  formInitValues,
  formRef,
  isOpen = false,
  isReturnCsv = true,
  maxCount = 1,
  schema,
  submittable,
  template,
  onCancel,
  onUpload,
  setSubmittable
}) {
  const [fileList, setFileList] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [csvData, setCsvData] = useState('');

  const [form] = Form.useForm();

  if (formRef) formRef.current = form;

  const values = Form.useWatch([], form);

  const { data, error, loading, parseFile } = useExcelParser();
  const { validData, errors, validateData } = useDataValidator(schema);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [values]);

  useEffect(() => {
    setValidationErrors(errors);
  }, [errors]);

  const props = {
    beforeUpload: async file => {
      const isXLSX = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      if (!isXLSX) {
        setFileList([]);
        form.setFields([{ name: 'fileList', errors: ['Only .xlsx files are allowed'] }]);

        return Upload.LIST_IGNORE;
      }

      form.setFields([{ name: 'fileList', errors: [] }]);

      return false;
    }
  };

  async function handleChange({ file, fileList }) {
    if (!file) return;

    setValidationErrors([]);
    setFileList(fileList);

    try {
      const parsed = await parseFile(file);
      await validateData(parsed?.json);

      setCsvData(parsed?.csv);
      setFileList(fileList);
    } catch (error) {
      setSubmittable(false);
    }
  }

  function handleConfirm() {
    const data = maxCount === 1 ? fileList?.at(0)?.originFileObj : fileList;

    onUpload(isReturnCsv ? csvData : data);
    setFileList([]);
  }

  function handleCancel() {
    onCancel();
    setFileList([]);
    setValidationErrors([]);
  }

  return (
    <AppModal
      isOpen={isOpen}
      confirmButtonDisabled={!fileList?.length || !submittable || errors?.length}
      confirmButtonText="Upload"
      title="Upload XLSX File"
      handleCancel={handleCancel}
      handleConfirm={handleConfirm}>
      <div className="py-4">
        {description && <div className={classnames({ 'mb-4': description })}>{description}</div>}
        <Form form={form} initialValues={formInitValues} labelCol={{ span: 6 }}>
          {template && fileName && <CsvDownload className="mb-4" template={template} fileName={fileName} />}
          {children}
          <Form.Item
            label="XLSX file"
            labelAlign="left"
            name="fileList"
            valuePropName="fileList"
            getValueFromEvent={e => (Array.isArray(e) ? e : e?.fileList)}
            colon={false}
            rules={[{ required: true, message: messages.FIELD_REQUIRED('File') }]}>
            <Upload
              {...props}
              fileList={fileList}
              showUploadList={fileList.length > 0}
              className={styles.uploader}
              maxCount={maxCount}
              onChange={handleChange}>
              <Button block icon={<UploadOutlined />}>
                Click to Upload File
              </Button>
            </Upload>
          </Form.Item>
          {validationErrors?.length > 0 && (
            <Form.Item
              label={
                <span>
                  <ExclamationOutlined style={{ color: '#e73a1e', marginLeft: '-4px' }} />
                  Validation failed
                </span>
              }
              labelAlign="left"
              colon={false}>
              {validationErrors?.map((error, index) => {
                if (typeof error === 'string') {
                  return (
                    <Text type="danger" key={index}>
                      {error}
                    </Text>
                  );
                }

                if (error && typeof error === 'object' && 'row' in error) {
                  return (
                    <Flex style={{ marginTop: '5px' }} className="mb-4" vertical={true} key={index}>
                      <Text>Row {error.row}:</Text>
                      <div>
                        {error.errors?.map(msg => {
                          return (
                            <Text className="d-block" type="danger" key={msg}>
                              {msg}
                            </Text>
                          );
                        })}
                      </div>
                    </Flex>
                  );
                }
              })}
            </Form.Item>
          )}
        </Form>
      </div>
    </AppModal>
  );
}

export default memo(UploadModal);
